<template>
  <v-form ref="form" v-model="valid">
    <v-text-field
      v-model="localValue.name"
      type="text"
      class="mb-6"
      :rules="[required()]"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.name.label`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.name.label`)"
      :aria-label="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.name.label`)"
      variant="outlined"
    >
    </v-text-field>
    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_REPORT}.reportType`) }}
    </div>
    <v-select
      v-model="localValue.subType"
      class="mb-6"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.reportType`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.reportType`)"
      :items="reportTypes"
      :rules="[required()]"
      variant="outlined"
      :return-object="false"
      :disabled="isEditMode"
    >
      <template #selection="{ item }">
        {{
          item.value
            ? $t(`reports.scheduled.subTypes.${item.value}`)
            : $t(`modals.${ScheduledReportsAction.ADD_REPORT}.reportType`)
        }}
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="$t(`reports.scheduled.subTypes.${item.value}`)">
        </v-list-item>
      </template>
    </v-select>

    <scheduled-report-modal-filters
      v-if="localValue.subType"
      class="mb-6"
      v-model:filters="localValue.filters"
      :report-type="localValue.subType"
    />

    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_REPORT}.frequency`) }}
    </div>

    <button-toggle-group
      v-model="localValue.frequency"
      :items="frequencyItems"
      class="mb-6 flex-grow-1 w-100"
      density="comfortable"
      bg-color="indigo-faint"
    />

    <template v-if="isChannelWorkspace">
      <div class="subtitle1 mb-4">
        {{ $t(`modals.${ScheduledReportsAction.ADD_REPORT}.scope`) }}
      </div>
      <v-radio-group v-model="localValue.scope" hide-details>
        <v-radio v-for="scope in scopes" class="mb-4" :key="scope" :value="scope" :ripple="false">
          <template #label>
            <div class="body1">
              {{ $t(`reports.scheduled.scopes.${scope}`) }}
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </template>

    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_REPORT}.recipients`) }}
    </div>
    <v-radio-group v-model="recipientsChoice" hide-details>
      <v-radio
        v-for="choice in RecipientsChoices"
        class="mb-4"
        :key="choice"
        :value="choice"
        :ripple="false"
      >
        <template #label>
          <div class="body1">
            {{ $t(`modals.${ScheduledReportsAction.ADD_REPORT}.recipientsChoice.${choice}`) }}
          </div>
        </template>
      </v-radio>
    </v-radio-group>

    <v-autocomplete
      v-if="recipientsChoice === RecipientsChoices.SPECIFIC_ADMIN_USERS"
      v-model="localValue.recipients"
      class="ml-10"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.recipients`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_REPORT}.recipients`)"
      item-title="email"
      item-value="email"
      :hide-no-data="false"
      :no-filter="true"
      auto-select-first="exact"
      :rules="[atLeastOneIsRequired()]"
      multiple
      :menu-props="{
        maxHeight: '250',
      }"
      :items="adminUsers"
      variant="outlined"
      chips
      closable-chips
      clear-on-select
      @update:search="onAdminUserSearch($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          color="indigo-faint"
          close-icon="$closeCircle"
          :text="item.title"
        >
          <div class="d-flex align-center">
            <span class="ml-1 mr-1">{{ item.title }}</span>
          </div>
        </v-chip>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item.title"></v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, watch } from "vue";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import {
  defaultScheduledReportFilters,
  ScheduledReportFrequency,
  type ScheduledReportItem,
  ScheduledType,
} from "@/_store/scheduled-reports/scheduled-reports.module.ts";
import { ScheduledReportsAction } from "@/constants/scheduled-reports";
import { useScheduledReportModal } from "@/composables/useScheduledReportModal";
import { ReportType } from "@/constants/reports.ts";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";
import ScheduledReportModalFilters from "@/components/modals/scheduled-reports/ScheduledReportModalFilters.vue";

const defaultScheduledReportItem = {
  id: undefined,
  name: undefined,
  subType: ReportType.EXECUTIVE_SUMMARY,
  filters: cloneDeep(defaultScheduledReportFilters),
  frequency: ScheduledReportFrequency.DAILY,
  scope: undefined,
  recipients: [],
  type: ScheduledType.REPORT,
} as unknown as ScheduledReportItem;

export default defineComponent({
  components: { ButtonToggleGroup, ScheduledReportModalFilters },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<ScheduledReportItem>>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const isEditMode = props.config.action === ScheduledReportsAction.EDIT_REPORT;
    const {
      valid,
      validate,
      localValue,
      isChannelWorkspace,
      adminUsersLoading,
      adminUsers,
      onAdminUserSearch,
      recipientsChoice,
      RecipientsChoices,
      form,
      frequencies,
      scopes,
    } = useScheduledReportModal(props.config.item, defaultScheduledReportItem, isEditMode);

    const frequencyItems = computed<ButtonToggleItem[]>(() =>
      frequencies.value.map((frequency) => ({
        value: frequency,
        label: t(`reports.scheduled.frequencies.${frequency}`),
      }))
    );

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value: boolean | null) => {
      emit("update:valid", value ?? true);
    });

    return {
      valid,
      form,
      recipientsChoice,
      RecipientsChoices,
      isChannelWorkspace,
      localValue,
      reportTypes: [ReportType.EXECUTIVE_SUMMARY],
      ScheduledReportFrequency,
      ScheduledReportsAction,
      adminUsers,
      onAdminUserSearch,
      required,
      validate,
      atLeastOneIsRequired,
      adminUsersLoading,
      isEditMode,
      frequencies,
      frequencyItems,
      scopes,
    };
  },
});
</script>

<style lang="scss" scoped>
.filter {
  width: 240px !important;
}
</style>
